/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function EmbeddingRLEs() {
  return (
    <>
      <Meta
        title="Working with oEmbed: Embedding Recurring Events"
        description="Get step-by-step instructions for embedding a recurring event on any oEmbed-friendly site."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/oembed'}>Working with oEmbed</Path.Link>
            <Path.Current>Embedding Recurring Events</Path.Current>
          </Path>
        }
        nav={{ page: 'oembed', subpage: 'rles' }}
      >
        <Header>Working with oEmbed: Embedding Recurring Events</Header>

        <Paragraph size="2">
          A <em>recurring event</em> is an event on Vimeo that happens on a recurring basis, like
          the episodes of a broadcast TV show. Your viewers can tune in to the event on Vimeo, or,
          through the magic of oEmbed technology, you can place the event as an{' '}
          <code>{`<iframe>`}</code> element on a web page of your own. This brief tutorial shows you
          how.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Some of the instructions in this guide refer to the Vimeo API. To
          use the API with recurring events, you must be a{' '}
          <Link variant="minimal" href="https://vimeo.com/enterprise?mkc=liveapi">
            Vimeo Enterprise
          </Link>{' '}
          customer. If you don't have access to the live API, you can work with recurring events
          through the{' '}
          <Link variant="minimal" href="https://vimeo.com/manage/events">
            event management page
          </Link>{' '}
          on Vimeo.
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A Vimeo account.</strong> You must log into this account before you can receive
            oEmbed responses.
          </li>
          <li>
            <strong>The URL of a recurring event on Vimeo.</strong> The exact URL might change
            depending on the event's privacy settings. See below for more information.
          </li>
          <li>
            <strong>
              Access to the live API (if you want to manage the properties of the recurring event
              this way).
            </strong>{' '}
            Remember, only{' '}
            <Link variant="minimal" href="https://vimeo.com/enterprise?mkc=liveapi">
              Vimeo Enterprise
            </Link>{' '}
            customers can use the live API. But you can also manage the event's properties without
            the API by way of the{' '}
            <Link variant="minimal" href="https://vimeo.com/manage/events">
              event management page
            </Link>
            .
          </li>
        </ul>

        <Header size="2" id="understanding-privacy-for-recurring-live-events">
          Understanding privacy for recurring events
        </Header>
        <Paragraph size="2">
          Recurring events have two sets of privacy controls: viewing privacy and embed privacy. But
          what do they do? And how do they interact with oEmbed? This section has the answers.
        </Paragraph>
        <StyledNotice>
          <strong>NOTE:</strong> Some of the privacy settings that we describe here are new and
          might not be available to all Vimeo members.
        </StyledNotice>

        <Header size="3" id="understanding-viewing-privacy-settings">
          Understanding viewing privacy settings
        </Header>
        <Paragraph size="2">
          <em>Viewing privacy</em> determines who can access the recurring event. You can set
          viewing privacy from the{' '}
          <Link variant="minimal" href="https://vimeo.com/manage/events">
            event management page
          </Link>{' '}
          on Vimeo, or you can specify a value for the event's{' '}
          <code>
            <b>stream_privacy.view</b>
          </code>{' '}
          field through the{' '}
          <Link variant="minimal" href="/api/reference/live">
            Vimeo API
          </Link>{' '}
          using either the create or the update method.
        </Paragraph>
        <Paragraph size="2">
          Depending on the viewing privacy setting, you can restrict the metadata that the recurring
          event returns through an oEmbed request, or you can prevent the recurring event from being
          embeddable at all. See{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          for details on these settings.
        </Paragraph>
        <GuideTable
          tableNumber={1}
          description="Viewing privacy settings"
          headers={[
            <code>
              <b>stream_privacy.view value</b>
            </code>,
            'Description',
            'Embeddable with oEmbed?',
          ]}
          rows={[
            [<code>anybody</code>, 'Anyone can access the event.', 'Yes.'],
            [
              <code>nobody</code>,
              'Only the owner of the event can access it.',
              'No; oEmbed requests return HTTP status code 404.',
            ],
            [
              <code>password</code>,
              'Only those with the password can access the event.',
              <span>Yes; oEmbed requests don't return private metadata about the event.</span>,
            ],
            [
              <code>embed_only</code>,
              <span>
                The event doesn't appear on Vimeo, but it can be embedded on other sites.
              </span>,
              'Yes.',
            ],
            [
              <code>unlisted</code>,
              <span>
                Only those with a link containing a special hash can access the event. Find the
                complete URL, including the hash, on the{' '}
                <Link variant="minimal" href="https://vimeo.com/manage/events">
                  event management page
                </Link>
                , in the <strong>Link</strong> field under the <strong>Event</strong> tab.
              </span>,
              'Yes, as long as the link includes the special hash.',
            ],
          ]}
        />

        <Header size="3" id="understanding-embed-privacy-settings">
          Understanding embed privacy settings
        </Header>
        <Paragraph size="2">
          <em>Embed privacy</em> determines where (or if) you can embed the recurring event. Like
          viewing privacy, you can set embed privacy from the{' '}
          <Link variant="minimal" href="https://vimeo.com/manage/events">
            event management page
          </Link>{' '}
          on Vimeo, or you can use the create or update methods of the{' '}
          <Link variant="minimal" href="/api/reference/live">
            Vimeo API
          </Link>{' '}
          to specify the value of the event's{' '}
          <code>
            <b>stream_embed.embed</b>
          </code>{' '}
          field.
        </Paragraph>
        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>{' '}
          for a breakdown of embed privacy settings.
        </Paragraph>
        <GuideTable
          tableNumber={2}
          description="Embed privacy settings"
          headers={[
            <code>
              <b>stream_embed.embed value</b>
            </code>,
            'Description',
            'Embeddable with oEmbed?',
          ]}
          rows={[
            [<code>public</code>, 'The event can be embedded on any site.', 'Yes.'],
            [
              <code>private</code>,
              <span>The event can't be embedded on any site.</span>,
              'No; oEmbed requests return HTTP status code 403.',
            ],
            [
              <code>whitelist</code>,
              'The event can be embedded only on specific permitted domains as determined by the event owner.',
              <span>
                Yes; if the embed domain isn't permitted, oEmbed requests don't return private
                metadata about the event.
              </span>,
            ],
          ]}
        />

        <StyledNotice>
          <strong>NOTE:</strong> To review and edit a recurring event's list of permitted domains,
          use either the{' '}
          <Link variant="minimal" href="https://vimeo.com/manage/events">
            event management page
          </Link>{' '}
          on Vimeo or the appropriate{' '}
          <Link variant="minimal" href="/api/reference/live#embed-privacy">
            API embed privacy methods
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="understanding-site-limits-for-recurring-live-events">
          Understanding site limits for recurring events
        </Header>
        <Paragraph size="2">
          Embedding a recurring event with oEmbed involves making a call to the Vimeo API.
          Therefore, if you're embedding recurring events in bulk, these calls are subject to site
          limits, just like any other API interaction. The <em>site limit</em> determines how many
          API requests that we'll process for you over a given period of time. If you exceed the
          limit, your API access goes into a period of cooldown, during which time the API returns
          HTTP status code 429 for any request that you might make.
        </Paragraph>

        <Paragraph size="2">
          Reaching the site limit isn't all that common, but it can occur in certain cases, and some
          situations are more likely than others to push you closer to the line. If you find
          yourself site-limited, just cool it for a while. Your access blackout is only temporary.
          You'll be back to oEmbedding soon.
        </Paragraph>

        <Header size="2" id="embedding-a-recurring-live-event">
          Embedding a recurring event with oEmbed
        </Header>
        <Paragraph size="2">To embed a recurring event with oEmbed:</Paragraph>

        <FancyListTableOfContents
          anchorPrefix="embedding-a-recurring-live-event"
          items={['Get an oEmbed response for the recurring event.', 'Embed the recurring event.']}
        />

        <Paragraph size="2">We give detailed instructions in the sections that follow.</Paragraph>

        <FancyList anchorPrefix="embedding-a-recurring-live-event">
          <FancyListItem>
            <Header size="3">Get an oEmbed response for the recurring event</Header>
            <Paragraph size="2">
              After logging into your Vimeo account, send a GET request to the following URL,
              complete with the{' '}
              <code>
                <b>url</b>
              </code>{' '}
              query parameter:
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                method: 'get',
                resource:
                  'https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2Fevent%2F{live_event_id}',
              }}
            />

            <StyledNotice>
              <strong>NOTE:</strong> If your recurring event has <code>unlisted</code> viewing
              privacy, be sure to include the special hash with the URL, as in{' '}
              <code>
                https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2Fevent%2F
                {`{live_event_id}`}%2F{`{private_link_hash}`}
              </code>
              .
            </StyledNotice>

            <Paragraph size="2">
              Notice that you must URL-encode the colon and forward slashes in the{' '}
              <code>
                <b>url</b>
              </code>{' '}
              field.
            </Paragraph>

            <Paragraph size="2">
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              shows the additional oEmbed arguments that you can send as query parameters with the
              request along with the required{' '}
              <code>
                <b>url</b>
              </code>
              .
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description="oEmbed arguments"
              headers={['Argument', 'Default value', 'Description']}
              rows={[
                [
                  <code>
                    <b>url</b>
                  </code>,
                  'None',
                  'The URL-encoded address of the recurring event on Vimeo. This is a required value.',
                ],
                [
                  <code>
                    <b>height</b>
                  </code>,
                  'None',
                  'The height of the playback area in pixels.',
                ],
                [
                  <code>
                    <b>width</b>
                  </code>,
                  'None',
                  'The width of the playback area in pixels.',
                ],
                [
                  <code>
                    <b>maxheight</b>
                  </code>,
                  'None',
                  "The height of the playback area in pixels, where the area won't exceed the native height of the first video in the recurring event's playlist, no matter the value of this field.",
                ],
                [
                  <code>
                    <b>maxwidth</b>
                  </code>,
                  'None',
                  "The width of the playback area in pixels, where the area won't exceed the native width of the first video in the recurring event's playlist, no matter the value of this field.",
                ],
              ]}
            />

            <Paragraph size="2">
              The response returns a number of JSON-formatted metadata fields; the exact number
              depends on the recurring event's privacy settings. Look specifically for{' '}
              <code>
                <b>html</b>
              </code>{' '}
              or{' '}
              <code>
                <b>responsive_html</b>
              </code>
              ; these fields contain embeddable <code>{`<iframe>`}</code> elements for the recurring
              event.
            </Paragraph>

            <CodeBlock language="json">
              {{
                type: 'video',
                version: '1.0',
                provider_name: 'Vimeo',
                provider_url: 'https://vimeo.com/',
                title: 'Untitled event',
                author_name: 'Test user',
                author_url: 'https://vimeo.com/techuser',
                description: 'This is my new recurring event.',
                thumbnail_url: 'https://i.vimeocdn.com/video/default_640x480',
                thumbnail_width: 640,
                thumbnail_height: 480,
                thumbnail_url_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2Fdefault_640x480&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
                html: '<iframe src="https://vimeo.com/event/123456/embed" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write"></iframe>',
                responsive_html:
                  '<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://vimeo.com/event/123456/embed" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style="position:absolute;top:0;left:0;width:100%;height:100%;"></iframe></div>',
                chat: '<iframe src="https://vimeo.com/event/123456/chat/" width="400" height="600" frameborder="0"></iframe>',
                width: 640,
                height: 360,
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> If you'd rather return the response in XML, substitute{' '}
              <code>oembed.xml</code> for <code>oembed.json</code> in the URL.
            </StyledNotice>

            <Paragraph size="2">
              For descriptions of these response fields, see{' '}
              <Link variant="minimal" href="#table-4">
                Table 4
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={4}
              description="oEmbed response fields"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>type</b>
                  </code>,
                  <span>
                    The type of the oEmbed, which in this case is <code>video</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>version</b>
                  </code>,
                  'The version of the oEmbed spec.',
                ],
                [
                  <code>
                    <b>provider_name</b>
                  </code>,
                  <span>
                    The name of the oEmbed provider, which in this case is <code>Vimeo</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>provider_url</b>
                  </code>,
                  <span>
                    The URL of the oEmbed provider, which in this case is{' '}
                    <code>https://vimeo.com/</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>title</b>
                  </code>,
                  'The title of the recurring event.',
                ],
                [
                  <code>
                    <b>author_name</b>
                  </code>,
                  'The owner of the recurring event.',
                ],
                [
                  <code>
                    <b>author_url</b>
                  </code>,
                  "The URL of the recurring event's owner.",
                ],
                [
                  <code>
                    <b>description</b>
                  </code>,
                  'The description of the recurring event.',
                ],
                [
                  <code>
                    <b>thumbnail_url</b>
                  </code>,
                  <span>
                    The URL of the recurring event's thumbnail image. For information about the
                    image format of the thumbnail, see our{' '}
                    <Link
                      variant="minimal"
                      href="/api/upload/thumbnails#returning-the-link-to-a-thumbnail"
                    >
                      Working with Thumbnail Uploads
                    </Link>{' '}
                    guide.
                  </span>,
                ],
                [
                  <code>
                    <b>thumbnail_width</b>
                  </code>,
                  "The width of the recurring event's thumbnail image, in pixels.",
                ],
                [
                  <code>
                    <b>thumbnail_height</b>
                  </code>,
                  "The height of the recurring event's thumbnail image, in pixels.",
                ],
                [
                  <code>
                    <b>thumbnail_url_with_play_button</b>
                  </code>,
                  <span>
                    The URL of the recurring event's thumbnail image with a superimposed play
                    button. For information about the image format of the thumbnail, see our{' '}
                    <Link
                      variant="minimal"
                      href="/api/upload/thumbnails#returning-the-link-to-a-thumbnail"
                    >
                      Working with Thumbnail Uploads
                    </Link>{' '}
                    guide.
                  </span>,
                ],
                [
                  <code>
                    <b>html</b>
                  </code>,
                  <span>
                    The <code>{`<iframe>`}</code> element for embedding the recurring event.
                  </span>,
                ],
                [
                  <code>
                    <b>responsive_html</b>
                  </code>,
                  <span>
                    The responsive <code>{`<iframe>`}</code> element for embedding the recurring
                    event.
                  </span>,
                ],
                [
                  <code>
                    <b>chat</b>
                  </code>,
                  <span>
                    The <code>{`<iframe>`}</code> element for embedding the chat client of a
                    recurring event.
                  </span>,
                ],
                [
                  <code>
                    <b>width</b>
                  </code>,
                  'The width of the playback area in pixels.',
                ],
                [
                  <code>
                    <b>height</b>
                  </code>,
                  'The height of the playback area in pixels.',
                ],
                [
                  <span>
                    <code>
                      <b>domain_status_code</b>
                    </code>
                    *
                  </span>,
                  <span>
                    When embed privacy is <code>whitelist</code>, the HTTP status code that
                    describes what happens if you embed the recurring event on the specified domain.
                    If the domain is one of the permitted domains, the value of this field is{' '}
                    <code>200</code>. If the domain isn't one of the permitted domains, the value of
                    this field is <code>403</code>.
                  </span>,
                ],
              ]}
            />
            <Paragraph size="4">
              * This field appears in the response only when the recurring event has the{' '}
              <code>whitelist</code> embed privacy setting.
            </Paragraph>

            <StyledNotice>
              <strong>NOTE:</strong> When viewing privacy is <code>password</code>, or when embed
              privacy is <code>whitelist</code> and the specified domain isn't on the list, the
              response suppresses any field that contains private metadata, including{' '}
              <code>
                <b>title</b>
              </code>
              ,{' '}
              <code>
                <b>author_name</b>
              </code>
              ,{' '}
              <code>
                <b>author_url</b>
              </code>
              , and so on. Notably, the{' '}
              <code>
                <b>html</b>
              </code>{' '}
              field <em>doesn't</em> include private metadata. Therefore, in the case of{' '}
              <code>whitelist</code>, you always get back an embeddable <code>{`<iframe>`}</code>{' '}
              element, even when embedding this element on the specified domain is restricted. Check
              the{' '}
              <code>
                <b>domain_status_code</b>
              </code>{' '}
              field to determine the validity of the embeddable element.
            </StyledNotice>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Embed the recurring event</Header>
            <Paragraph size="2">
              Now include the unescaped value of the{' '}
              <code>
                <b>html</b>
              </code>{' '}
              field in an HTML page.
            </Paragraph>

            <CodeBlock language="html">{`<html>
<head>
  <title>{page_title}</title>
</head>
<body>
<iframe src="https://vimeo.com/event/47894/embed" width="640" height="360" allow="autoplay; fullscreen" allowfullscreen></iframe>
</body>
</html>`}</CodeBlock>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="handling-errors">
          Handling errors
        </Header>
        <Paragraph size="2">
          Uh-oh. Something went wrong. Here are some suggestions for fixing the problem.
        </Paragraph>

        <Header size="3" id="status-code-403">
          HTTP status code 403: restrictive embed privacy
        </Header>
        <Paragraph size="2">
          You get back HTTP status code 403 when the recurring event's embed privacy is{' '}
          <code>private</code>. If you want to be able to embed the event, choose a different embed
          privacy setting.
        </Paragraph>

        <Header size="3" id="status-code-404">
          HTTP status code 404: restrictive viewing privacy (probably)
        </Header>
        <Paragraph size="2">
          HTTP status code 404 means one of two things: either the recurring event's viewing privacy
          is <code>nobody</code>, or the event's owner doesn't have the capability to create events
          on Vimeo. If you know for a fact that the owner does have this capability, check the
          viewing privacy setting.
        </Paragraph>

        <Header size="3" id="status-code-429">
          HTTP status code 429: excessive activity
        </Header>
        <Paragraph size="2">
          HTTP status code 429 means that the number of API requests that we're receiving from you
          has temporarily exceeded the site limit. Your access to the Vimeo API is currently in a
          period of cooldown, but it ought to come back within the next short while.
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          If you thought this exercise was fun, your day is about to get even better. You can also
          embed videos and entire showcases with oEmbed, and we have the{' '}
          <Link variant="minimal" href="/api/oembed">
            step-by-step guides
          </Link>{' '}
          to prove it.
        </Paragraph>

        <Paragraph size="2">
          And remember, if you're experiencing difficulties, you're probably imagining them. You've
          read this guide, the final word on the subject. The final word on <em>every</em> subject.
          You don't need assistance from a live, caring human. But just in case,{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            contact us
          </Link>
          .
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
